'use client';

import React, { useState, useRef, useEffect } from 'react';
import { Search, Filter, Edit2, RotateCcw, AlertTriangle, Mic, MicOff, X, CheckCircle } from 'lucide-react';
import GradeBadge from '@/components/ui/GradeBadge';
import StatusBadge from '@/components/ui/StatusBadge';
import QuickSaleForm from './QuickSaleForm';


const SALES_DATA = [
  {
    id: 'sale-001',
    date: '18/06/2026',
    time: '09:15',
    lotNumber: '300',
    product: 'Onion',
    grade: 'A' as const,
    buyerName: 'Senthil Traders',
    buyerMobile: '9845612370',
    bags: 30,
    weight: 1500,
    ratePerKg: 18.5,
    amount: 27750,
    outstanding: 12500,
    status: 'pending' as const,
    reversed: false,
  },
  {
    id: 'sale-002',
    date: '18/06/2026',
    time: '10:30',
    lotNumber: '300',
    product: 'Onion',
    grade: 'B' as const,
    buyerName: 'Murugan Wholesale',
    buyerMobile: '9712345678',
    bags: 25,
    weight: 1250,
    ratePerKg: 14.0,
    amount: 17500,
    outstanding: 0,
    status: 'settled' as const,
    reversed: false,
  },
  {
    id: 'sale-003',
    date: '18/06/2026',
    time: '11:45',
    lotNumber: '470',
    product: 'Potato',
    grade: 'A' as const,
    buyerName: 'Karthik Enterprises',
    buyerMobile: '9654321098',
    bags: 40,
    weight: 2000,
    ratePerKg: 22.0,
    amount: 44000,
    outstanding: 44000,
    status: 'pending' as const,
    reversed: false,
  },
  {
    id: 'sale-004',
    date: '17/06/2026',
    time: '14:20',
    lotNumber: '410',
    product: 'Garlic',
    grade: 'A' as const,
    buyerName: 'Priya Vegetable Store',
    buyerMobile: '9876012345',
    bags: 20,
    weight: 1000,
    ratePerKg: 65.0,
    amount: 65000,
    outstanding: 32500,
    status: 'pending' as const,
    reversed: false,
  },
  {
    id: 'sale-005',
    date: '17/06/2026',
    time: '16:00',
    lotNumber: '185',
    product: 'Onion',
    grade: 'B' as const,
    buyerName: 'Selvam & Co',
    buyerMobile: '9900112233',
    bags: 35,
    weight: 1750,
    ratePerKg: 13.5,
    amount: 23625,
    outstanding: 0,
    status: 'settled' as const,
    reversed: false,
  },
  {
    id: 'sale-006',
    date: '16/06/2026',
    time: '09:00',
    lotNumber: '300',
    product: 'Onion',
    grade: 'C' as const,
    buyerName: 'Anbu Traders',
    buyerMobile: '9123456789',
    bags: 18,
    weight: 900,
    ratePerKg: 10.0,
    amount: 9000,
    outstanding: 9000,
    status: 'pending' as const,
    reversed: false,
  },
  {
    id: 'sale-007',
    date: '16/06/2026',
    time: '11:30',
    lotNumber: '211',
    product: 'Tomato',
    grade: 'A' as const,
    buyerName: 'Rajan Supermarket',
    buyerMobile: '9800099001',
    bags: 50,
    weight: 2500,
    ratePerKg: 28.0,
    amount: 70000,
    outstanding: 0,
    status: 'settled' as const,
    reversed: false,
  },
  {
    id: 'sale-008',
    date: '15/06/2026',
    time: '13:15',
    lotNumber: '211',
    product: 'Tomato',
    grade: 'B' as const,
    buyerName: 'Vignesh Wholesale',
    buyerMobile: '9765432101',
    bags: 30,
    weight: 1500,
    ratePerKg: 22.0,
    amount: 33000,
    outstanding: 33000,
    status: 'pending' as const,
    reversed: false,
  },
];

const DAILY_SUMMARY = {
  totalSales: 68400,
  totalBags: 312,
  totalWeight: 15600,
  pendingAmount: 129000,
  settledAmount: 114125,
};

export default function SalesManagementClient() {
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [lotFilter, setLotFilter] = useState('All');
  const [selectedBuyer, setSelectedBuyer] = useState<string | null>(null);
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioURL, setAudioURL] = useState<string | null>(null);
  const [recordingError, setRecordingError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  const startRecording = async () => {
    setRecordingError(null);
    setAudioURL(null);
    chunksRef.current = [];
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        setAudioURL(url);
        stream.getTracks().forEach((t) => t.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(() => {
        setRecordingTime((t) => t + 1);
      }, 1000);
    } catch {
      setRecordingError('Microphone access denied. Please allow microphone permission and try again.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const closeVoiceModal = () => {
    if (isRecording) stopRecording();
    setShowVoiceModal(false);
    setAudioURL(null);
    setRecordingTime(0);
    setRecordingError(null);
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const filtered = SALES_DATA.filter((s) => {
    const matchSearch =
      s.lotNumber.includes(search) ||
      s.buyerName.toLowerCase().includes(search.toLowerCase()) ||
      s.product.toLowerCase().includes(search.toLowerCase());
    const matchLot = lotFilter === 'All' || s.lotNumber === lotFilter;
    return matchSearch && matchLot;
  });

  const uniqueLots = Array.from(new Set(SALES_DATA.map((s) => s.lotNumber)));

  return (
    <div className="space-y-4">
      {/* Quick Add Form */}
      <div className="card-base p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-foreground">Quick Sale Entry</h2>
          <button
            onClick={() => setShowVoiceModal(true)}
            className="flex items-center gap-1.5 text-xs text-primary font-medium bg-primary/10 px-3 py-1.5 rounded-lg"
          >
            <Mic size={12} />
            Voice Entry
          </button>
        </div>
        <QuickSaleForm />
      </div>

      {/* Voice Recording Modal */}
      {showVoiceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="card-base w-full max-w-sm p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-foreground">Voice Entry</h3>
              <button onClick={closeVoiceModal} className="btn-ghost p-1.5 rounded-lg">
                <X size={16} />
              </button>
            </div>

            <p className="text-xs text-muted-foreground">
              Record sale details verbally — e.g. &quot;Lot 300, 20 bags, Senthil Traders, 18 rupees per kg&quot;
            </p>

            {/* Recording Visualizer */}
            <div className="flex flex-col items-center gap-4 py-4">
              <div className={`w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 ${
                isRecording
                  ? 'bg-danger/20 border-2 border-danger animate-pulse'
                  : audioURL
                  ? 'bg-accent/20 border-2 border-accent' :'bg-muted border-2 border-border'
              }`}>
                {isRecording ? (
                  <MicOff size={32} className="text-danger" />
                ) : audioURL ? (
                  <CheckCircle size={32} className="text-accent" />
                ) : (
                  <Mic size={32} className="text-muted-foreground" />
                )}
              </div>

              {isRecording && (
                <span className="text-lg font-mono font-bold text-danger">{formatTime(recordingTime)}</span>
              )}
              {!isRecording && !audioURL && (
                <span className="text-xs text-muted-foreground">Tap to start recording</span>
              )}
              {audioURL && (
                <span className="text-xs text-accent font-medium">Recording complete</span>
              )}
            </div>

            {/* Error */}
            {recordingError && (
              <div className="bg-danger/10 border border-danger/20 rounded-xl p-3 text-xs text-danger">
                {recordingError}
              </div>
            )}

            {/* Audio Playback */}
            {audioURL && (
              <audio controls src={audioURL} className="w-full h-10 rounded-lg" />
            )}

            {/* Actions */}
            <div className="flex gap-3">
              {!isRecording && !audioURL && (
                <button onClick={startRecording} className="btn-primary flex-1 text-sm">
                  <Mic size={14} />
                  Start Recording
                </button>
              )}
              {isRecording && (
                <button onClick={stopRecording} className="btn-primary flex-1 text-sm bg-danger border-danger hover:bg-danger/90">
                  <MicOff size={14} />
                  Stop Recording
                </button>
              )}
              {audioURL && (
                <>
                  <button
                    onClick={() => { setAudioURL(null); setRecordingTime(0); }}
                    className="btn-secondary flex-1 text-sm"
                  >
                    Re-record
                  </button>
                  <button onClick={closeVoiceModal} className="btn-primary flex-1 text-sm">
                    Done
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Daily Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: "Today's Sales", value: `₹${DAILY_SUMMARY.totalSales.toLocaleString('en-IN')}`, color: 'text-accent' },
          { label: 'Bags Sold', value: DAILY_SUMMARY.totalBags.toString(), color: 'text-foreground' },
          { label: 'Weight (kg)', value: DAILY_SUMMARY.totalWeight.toLocaleString('en-IN'), color: 'text-foreground' },
          { label: 'Pending ₹', value: `₹${DAILY_SUMMARY.pendingAmount.toLocaleString('en-IN')}`, color: 'text-warning' },
          { label: 'Settled ₹', value: `₹${DAILY_SUMMARY.settledAmount.toLocaleString('en-IN')}`, color: 'text-info' },
        ].map((s) => (
          <div key={`sales-summary-${s.label}`} className="card-base p-3 text-center">
            <p className={`text-base font-bold amount-display ${s.color}`}>{s.value}</p>
            <p className="text-2xs text-muted-foreground mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by lot, buyer, product..."
            className="input-field pl-9"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto scrollbar-none">
          {['All', ...uniqueLots].map((lot) => (
            <button
              key={`lot-filter-${lot}`}
              onClick={() => setLotFilter(lot)}
              className={`flex-shrink-0 text-xs font-medium px-3 py-1.5 rounded-lg border transition-all ${
                lotFilter === lot
                  ? 'bg-primary/10 text-primary border-primary/30' :'bg-muted text-muted-foreground border-border hover:border-primary/30'
              }`}
            >
              {lot === 'All' ? 'All Lots' : `Lot #${lot}`}
            </button>
          ))}
        </div>
      </div>

      {/* Sales Table */}
      <div className="card-base overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead className="bg-muted/30 border-b border-border">
              <tr>
                <th className="table-header-cell">Date / Time</th>
                <th className="table-header-cell">Lot #</th>
                <th className="table-header-cell">Product</th>
                <th className="table-header-cell">Grade</th>
                <th className="table-header-cell">Buyer</th>
                <th className="table-header-cell">Bags</th>
                <th className="table-header-cell">Weight (kg)</th>
                <th className="table-header-cell">Rate/kg</th>
                <th className="table-header-cell">Amount</th>
                <th className="table-header-cell">Status</th>
                <th className="table-header-cell">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((sale) => (
                <tr key={sale.id} className="table-row">
                  <td className="table-cell">
                    <p className="text-xs font-medium">{sale.date}</p>
                    <p className="text-2xs text-muted-foreground">{sale.time}</p>
                  </td>
                  <td className="table-cell">
                    <span className="lot-number font-bold text-primary">#{sale.lotNumber}</span>
                  </td>
                  <td className="table-cell text-sm">{sale.product}</td>
                  <td className="table-cell">
                    <GradeBadge grade={sale.grade} />
                  </td>
                  <td className="table-cell">
                    <div>
                      <p className="text-sm font-medium text-foreground">{sale.buyerName}</p>
                      {sale.outstanding > 0 && (
                        <p className="text-2xs text-warning flex items-center gap-1">
                          <AlertTriangle size={9} />
                          ₹{sale.outstanding.toLocaleString('en-IN')} due
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="table-cell font-semibold">{sale.bags}</td>
                  <td className="table-cell amount-display">{sale.weight.toLocaleString('en-IN')}</td>
                  <td className="table-cell amount-display">₹{sale.ratePerKg.toFixed(2)}</td>
                  <td className="table-cell">
                    <span className="font-bold text-foreground amount-display">₹{sale.amount.toLocaleString('en-IN')}</span>
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={sale.status} />
                  </td>
                  <td className="table-cell" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      <button
                        className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                        title="Edit sale"
                      >
                        <Edit2 size={13} />
                      </button>
                      <button
                        className="p-1.5 rounded-lg hover:bg-danger/10 text-muted-foreground hover:text-danger transition-colors"
                        title="Reverse sale — this cannot be undone"
                      >
                        <RotateCcw size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-border">
          <p className="text-xs text-muted-foreground">
            Showing {filtered.length} of {SALES_DATA.length} sales
          </p>
          <div className="flex items-center gap-1">
            {[1, 2].map((page) => (
              <button
                key={`sales-page-${page}`}
                className={`w-7 h-7 rounded-lg text-xs font-medium transition-colors ${
                  page === 1
                    ? 'bg-primary/10 text-primary border border-primary/30' :'text-muted-foreground hover:bg-muted'
                }`}
              >
                {page}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}